import { makeStyles } from '@material-ui/core';
import { ArrowDropDown } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { CSSProperties, FC } from 'react';

import {
  useLayout,
  useProcessedBannerStore,
  useProcessedWorkStore,
} from '../../../studentApp/hooks';
import { getType } from '../../../studentApp/hooks/useProcessedWorkStore';
import {
  CHOOSE_WORK,
  studentAppModalStore,
} from '../../../studentApp/stores/studentAppModalStore';
import { workStore } from '../../../studentApp/stores/workStore';
import { Exam } from '../../../studentApp/types/types';
import {
  BANNER_HEIGHT,
  MAX_LANDSCAPE_BANNER_WORK_NAME_WIDTH,
  MAX_MOBILE_BANNER_WORK_NAME_WIDTH,
  MAX_PORTRAIT_BANNER_WORK_NAME_WIDTH,
} from '../../constants';
import { TextOnCanvas } from '../util/textOnCanvas';

const useStyles = makeStyles({
  workName: (props: StyleProps) => ({
    fontSize: props.fontSize,
    fontWeight: props.fontWeight,
    color: 'white',
    maxWidth: props.maxWidth,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    border: props.border,
    borderRadius: props.borderRadius,
    padding: props.padding,
    marginRight: props.marginRight,
  }),
});

const SPIRAL = 'GMM-icons/GMM-icon-transparent-spiral.svg';

export const WorkNameAndProgress: FC = () => {
  const {
    currentWorkName,
    currentWorkProgress,
    currentWorkType,
  } = useProcessedWorkStore();
  const { isLandscape, isPortrait } = useLayout();

  const classes = useStyles({
    maxWidth: isLandscape
      ? MAX_LANDSCAPE_BANNER_WORK_NAME_WIDTH
      : isPortrait
      ? MAX_PORTRAIT_BANNER_WORK_NAME_WIDTH
      : MAX_MOBILE_BANNER_WORK_NAME_WIDTH,
    fontSize: isLandscape ? '18px' : '14px',
    fontWeight: isLandscape ? 800 : 600,
  });

  const [work] = workStore(state => [state.currentWork]);
  const isFollowUp = getType(work) === 'EXAM' && (work as Exam).isFollowUp;
  const { themeColor } = useProcessedBannerStore();
  const { isTesting } = useProcessedWorkStore();

  return (
    <div style={workAndProgressStyle}>
      {isTesting && (
        <span className={classes.workName}>
          {isFollowUp
            ? currentWorkName.split(' (Follow-up)')[0]
            : currentWorkName}
        </span>
      )}
      {isTesting && isFollowUp && (
        <span
          className={classes.workName}
          style={{
            color: themeColor,
            backgroundColor: 'white',
            borderRadius: '10px',
            marginLeft: '5px',
            padding: '5px',
          }}
        >
          Follow-up
        </span>
      )}

      {currentWorkType === 'SPIRAL_REVIEW' && (
        <img
          src={SPIRAL}
          height={BANNER_HEIGHT - 20}
          style={{ marginRight: '5px' }}
        />
      )}

      {!isTesting && <WorkChooser />}

      {!isTesting && currentWorkType !== 'SPIRAL_REVIEW' && (
        <TextOnCanvas
          text={currentWorkProgress}
          bold={true}
          fontSize={isLandscape ? 24 : isPortrait ? 20 : 16}
          color={themeColor}
          id="workProgress"
          roundedRectangle={true}
          backColor="white"
        />
      )}
    </div>
  );
};

const workAndProgressStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

interface StyleProps {
  maxWidth: number;
  fontSize: string;
  fontWeight: number;
  border?: string;
  borderRadius?: string;
  padding?: string;
  marginRight?: string;
}

export const WorkChooser: FC = () => {
  const setModal = studentAppModalStore(state => state.setCurrentModal);
  const workName = useProcessedWorkStore().currentWorkName;
  const { isLandscape, isPortrait, isMobile } = useLayout();
  const { hasWorkChoices } = useProcessedWorkStore();

  const classes = useStyles({
    maxWidth: isLandscape
      ? MAX_LANDSCAPE_BANNER_WORK_NAME_WIDTH
      : isPortrait
      ? MAX_PORTRAIT_BANNER_WORK_NAME_WIDTH
      : MAX_MOBILE_BANNER_WORK_NAME_WIDTH,
    fontSize: isLandscape ? '18px' : '14px',
    fontWeight: isLandscape ? 800 : 600,
    border: '2px solid white',
    borderRadius: '10px',
    padding: isMobile ? '1px' : '5px',
    marginRight: '10px',
  });

  return (
    <>
      {!hasWorkChoices && <span className={classes.workName}>{workName}</span>}

      {hasWorkChoices && (
        <Tooltip title="Change work">
          <div
            className={classes.workName}
            onClick={() => setModal(CHOOSE_WORK)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span>{workName}</span>
            <ArrowDropDown sx={{ color: 'white' }} />
          </div>
        </Tooltip>
      )}
    </>
  );
};
